import React, {
  useCallback,
  useContext,
  useEffect,
  useState,
  useMemo,
  useRef,
} from "react";
import { useHistory } from "react-router-dom";
import { Select, SelectOption, Image, Link, Flex } from "uikit-react";
import UIkit from "uikit";
import moment from "moment";
import classnames from "classnames";

import { AppContexts } from "../../providers/";
import { useCookies } from "react-cookie";
import FeatherIcon from "../common/FeatherIcon";
import { useDebounce } from "../../hook";
import NewMessageService from "../../services/new-message.service";

import { SMSThreadCard } from "./thread-cards/sms.js";
import { AirbnbThreadCard } from "./thread-cards/airbnb.js";
import { ReservationThreadCard } from "./thread-cards/reservation.js";

const searchValue = {
  search_term: "",
  filters: "all",
  preset: "",
  start_date: "2000-01-01",
  end_date: "2050-12-31",
  end_type: "check_in",
  checked_in: false,
  checked_out: false,
  categories: "",
  type: "message",
};

const ContactSearchThreadCard = ({ contact, selected, handleSelect }) => {
  const iconName = contact.category || "owner";

  return (
    <div
      className={classnames("chat-guest-contact-wrapper", {
        "active-chat-guest-contact": selected,
      })}
      key={`contact-search-${contact.thread_id || contact.id}`}
      onClick={() => handleSelect(contact.thread_id)}
    >
      <div className="message-text-group">
        <Image src={`/images/${iconName}-active.svg`} />
        <div>
          <div className="chat-guest-contact-name">
            {contact.first_name} {contact.last_name}
          </div>
          <div className="chat-guest-contact-date">
            {contact.formatted_phone || contact.phone || contact.email || "Contact"}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ({ onNewMessage } = {}) => {
  const inboxContext = useContext(AppContexts.InboxContext);
  const { listReservations, searchReservation } = useContext(
    AppContexts.ReservationsContext,
  );

  const { user } = useContext(AppContexts.AuthContext);
  const { notification } = useContext(AppContexts.UtilContext);
  const { setNotes } = useContext(AppContexts.NotesContext);
  const [cookies] = useCookies(["records-per-page"]);
  const { "records-per-page": recordsPerPage = 20 } = cookies;

  const [option, setOption] = useState("");
  const [search, setSearch] = useState("");
  const [contactResults, setContactResults] = useState([]);
  const [pageNo, setPageNo] = useState(0);
  const [isFirst, setIsFirst] = useState(true);
  const [allThreads, setAllThreads] = useState([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const scrollContainerRef = useRef(null);
  const prevPageRef = useRef(0);
  const contactSearchRequestIdRef = useRef(0);
  const normalizedSearch = search.trim();
  const debouncedSearch = useDebounce(normalizedSearch, 400);
  const {
    inbox,
    selectedThreadId,
    setSelectedThreadId,

    selectThread,
    selectPaginatedThread,
    selectPersonalPaginatedThread,
    getThreadDetail,
    getPaginatedThreadDetail,
    getPersonalPaginatedThreadDetail,
    setThreadDetail,

    threadCnt,
    setThreadMeta,
    setReservation,

    nextThread,

    legacy,
    setLegacy,
  } = inboxContext;
  const history = useHistory();

  // Reset accumulated threads when filters change
  useEffect(() => {
    setAllThreads([]);
    setPageNo(0);
    prevPageRef.current = 0;
  }, [option, recordsPerPage, legacy]);

  useEffect(() => {
    if (!!(user.user.personal_inboxes || [])[0]) {
      inboxContext.personalInbox(
        user.user.personal_inboxes[0].id,
        pageNo,
        option,
        recordsPerPage,
      );
    } else {
      inboxContext.refresh(pageNo, option, recordsPerPage);
    }
  }, [option, recordsPerPage, pageNo, legacy]);

  // Accumulate threads as pages load
  useEffect(() => {
    if (inbox && inbox.length > 0) {
      setAllThreads((prev) => {
        if (pageNo === 0 && prevPageRef.current === 0) {
          return inbox;
        }
        // Deduplicate by id
        const existingIds = new Set(prev.map((t) => t.id));
        const newThreads = inbox.filter((t) => !existingIds.has(t.id));
        return [...prev, ...newThreads];
      });
      setLoadingMore(false);
      prevPageRef.current = pageNo;
    } else if (inbox && inbox.length === 0 && pageNo === 0) {
      setAllThreads([]);
      setLoadingMore(false);
    }
  }, [inbox]);

  useEffect(() => {
    if (!!nextThread) {
      if (personalInboxId !== null) {
        selectPersonalPaginatedThread(nextThread, personalInboxId);
      } else {
        selectPaginatedThread(nextThread);
      }
    }
  }, [inbox]);

  useEffect(() => {
    setIsFirst(false);
  }, []);

  const hasMore = allThreads.length < threadCnt;

  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container || loadingMore || !hasMore) return;
    const { scrollTop, scrollHeight, clientHeight } = container;
    if (scrollHeight - scrollTop - clientHeight < 150) {
      setLoadingMore(true);
      setPageNo((prev) => prev + 1);
    }
  }, [loadingMore, hasMore]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  const personalInboxId = useMemo(() => {
    if (!!(user.user.personal_inboxes || [])[0]) {
      return user.user.personal_inboxes[0].id;
    }

    return null;
  }, [user]);

  const toggleLegacy = () => {
    setLegacy(!legacy);
  };

  const handleSelect = (threadId) => {
    // let current = +window.location.pathname.split("/")[2];

    if (threadId === null) {
      history.push("/messages/new");
      setSelectedThreadId("new");
      setThreadDetail({});
    } else if (+selectedThreadId !== +threadId) {
      history.push(`/messages/${threadId}`);
      if (personalInboxId !== null) {
        selectPersonalPaginatedThread(threadId, personalInboxId);
      } else {
        selectPaginatedThread(threadId);
      }
    }

    // pushToRoom("update_status", {
    //   thread_id: threadId,
    //   personal_inbox_id: personalInboxId,
    //   status: "viewing",
    // });

    setNotes([]);
  };

  const handleSelectOption = (e) => {
    setOption(e.target.value);
  };

  const reservations = useMemo(() => {
    let sorts = listReservations?.sort((prev, next) => {
      return next.check_in?.localeCompare(prev.check_in);
    });
    let buf = [];
    sorts.forEach((sort) => {
      if (sort.thread_id === null) {
        buf.push(sort);
      } else {
        if (!buf.find((item) => item?.thread_id === sort.thread_id)) {
          buf.push(sort);
        }
      }
    });
    return buf;
  }, [listReservations]);

  const searchableContacts = useMemo(() => {
    const seenThreadIds = new Set();

    return contactResults.filter((contact) => {
      if (!contact?.thread_id || seenThreadIds.has(contact.thread_id)) {
        return false;
      }

      seenThreadIds.add(contact.thread_id);
      return true;
    });
  }, [contactResults]);

  const hasActiveSearch = debouncedSearch.length >= 2;

  const handleMetaCallback = async (result, thread_id, token) => {
    if (result) {
      let message;
      let next = null;
      // console.log(result.thread);
      if (token === "unread") {
        message = !!(result.thread.meta?.tokens || []).find(
          (t) => t === "unread",
        )
          ? "Message Marked as Unread"
          : "Message Marked as Read";
      } else if (token === "priority") {
        message = !!(result.thread.meta?.tokens || []).find(
          (t) => t === "priority",
        )
          ? "Message Prioritized"
          : "Message Deprioritized";
      } else {
        next = ((inbox || []).findIndex((t) => +t.id === +thread_id) || -1) + 1;
        message = !!(result.thread.meta?.tokens || []).find(
          (t) => t === "archived",
        )
          ? "Message Archived"
          : "Message Unarchived";
      }
      UIkit.notification(message, "success");

      if (!searchValue.search_term) {
        if (personalInboxId !== null) {
          await inboxContext.personalInbox(
            personalInboxId,
            pageNo,
            option,
            recordsPerPage,
            true,
            next,
          );
        } else {
          await inboxContext.refresh(
            pageNo,
            option,
            recordsPerPage,
            true,
            next,
          );
        }
      } else {
        searchReservation(searchValue);
      }
    }
  };

  useEffect(() => {
    if (debouncedSearch.length < 2) {
      contactSearchRequestIdRef.current += 1;
      setContactResults([]);
      return;
    }

    const currentSearch = debouncedSearch;
    searchValue.search_term = currentSearch;
    searchReservation({ ...searchValue, searchKey: "messages-inbox" });

    const requestId = contactSearchRequestIdRef.current + 1;
    contactSearchRequestIdRef.current = requestId;

    NewMessageService.searchContacts(currentSearch)
      .then((res) => {
        if (requestId !== contactSearchRequestIdRef.current) {
          return;
        }

        const paginated = res.data?.paginated || res.data || [];
        setContactResults(paginated.filter((contact) => !!contact?.thread_id));
      })
      .catch(() => {
        if (requestId !== contactSearchRequestIdRef.current) {
          return;
        }

        setContactResults([]);
      });
  }, [debouncedSearch, searchReservation]);

  useEffect(() => {
    if (!!notification) {
      if (personalInboxId !== null) {
        inboxContext.personalInbox(
          personalInboxId,
          pageNo,
          option,
          recordsPerPage,
          true,
        );
      } else {
        inboxContext.refresh(pageNo, option, recordsPerPage, true);
      }
      if (+notification.thread_id === +window.location.pathname.split("/")[2]) {
        if (personalInboxId !== null) {
          getPersonalPaginatedThreadDetail(
            notification.thread_id,
            personalInboxId,
          );
        } else {
          getPaginatedThreadDetail(notification.thread_id);
        }
      }
    }
  }, [notification]);

  const handleGuest = (e) => {
    setSearch(e.target.value);
  };

  const getThreadType = (thread) => {
    if (!thread.sources) {
      return "sms";
    }

    let sources = thread.sources.map((s) => s.ota);
    if (sources.includes("airbnb")) {
      return "airbnb";
    }
  };

  const handleIndexChanged = (newPageIndex) => {
    console.log("New Page Index", newPageIndex);
    if (!isFirst) {
      setSelectedThreadId(-1);
      setThreadDetail({});
      // setCurrentIndexOnPage(-1);
      history.push("/messages");
    }
    setPageNo(newPageIndex);
  };

  const togglePersonalMeta = async (thread_id, token) => {
    let result = null;
    if (!!(user.user.personal_inboxes || [])[0]) {
      result = await inboxContext.togglePersonalMeta(
        thread_id,
        user.user.personal_inboxes[0].id,
        token,
      );
    } else {
      result = await inboxContext.setMeta(thread_id, {
        thread_id,
        token: token,
      });
    }

    handleMetaCallback(result, thread_id, token);
  };

  const setPersonalMeta = async (thread_id, token, set) => {
    let result = null;
    if (!!(user.user.personal_inboxes || [])[0]) {
      result = await inboxContext.setPersonalMeta(
        thread_id,
        user.user.personal_inboxes[0].id,
        token,
        set,
      );
    } else {
      result = await inboxContext.setMeta(thread_id, {
        thread_id,
        token: token,
      });
    }

    handleMetaCallback(result, thread_id, token);
  };

  const handleMeta = async (thread_id, metaType, flag) => {
    let result = null;
    if (!!(user.user.personal_inboxes || [])[0]) {
      let result = await inboxContext.setPersonalMeta(thread_id, {
        thread_id,
        token: metaType,
      });
    } else {
      let result = await inboxContext.setMeta(thread_id, {
        thread_id,
        token: metaType,
      });
    }

    if (result) {
      let message;
      let next = null;
      if (metaType === "unread") {
        message = result.thread.meta?.find((item) => item.token === "unread")
          ? "Message Marked as Unread"
          : "Message Marked as Read";
      } else if (metaType === "priority") {
        message = result.thread.meta?.find((item) => item.token === "priority")
          ? "Message Prioritized"
          : "Message Deprioritized";
      } else {
        next = ((inbox || []).findIndex((t) => +t.id === +thread_id) || -1) + 1;
        message = result.thread.meta?.find((item) => item.token === "archived")
          ? "Message Archived"
          : "Message Unarchived";
      }
      UIkit.notification(message, "success");
      flag
        ? await inboxContext.personalInbox(
            pageNo,
            option,
            recordsPerPage,
            true,
            next,
          )
        : searchReservation(searchValue);
    }
  };

  return (
    <aside
      className="uk-card uk-card-default uk-card-small uk-card-body uk-width-1-4@m hms-msg-cards chat-guest-contact-card"
      style={{ display: "flex", flexDirection: "column", overflow: "hidden" }}
    >
      {!+user?.user?.owner_id && (
        <div className="inboxSelector">
          <button
            className={`uk-button hms-btn hms-blue-btn ${legacy ? "hms-blue-btn-transparent" : ""}`}
            onClick={toggleLegacy}
          >
            My Inbox
          </button>
          <button
            className={`uk-button hms-btn hms-blue-btn ${!legacy ? "hms-blue-btn-transparent" : ""}`}
            onClick={toggleLegacy}
          >
            All Messages
          </button>
        </div>
      )}
      <div className="chat-msg-type">
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <Select onChange={handleSelectOption}>
            <SelectOption value="">Inbox</SelectOption>
            <SelectOption value="priority">Priority</SelectOption>
            <SelectOption value="unread">Unread</SelectOption>
            <SelectOption value="archived">Archived</SelectOption>
            <SelectOption value="all">All Messages</SelectOption>
          </Select>
          {onNewMessage && (
            <span
              style={{
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
              }}
              onClick={onNewMessage}
              uk-tooltip="New Message"
            >
              <FeatherIcon name="plus-circle" className="inbox-new-msg-icon" />
            </span>
          )}
        </div>
        <div className="uk-search uk-search-default">
          {!search ? (
            <span className="uk-search-icon-flip" uk-search-icon="true"></span>
          ) : (
            <span
              className="uk-close-icon uk-close-icon-flip"
              uk-icon="close"
              onClick={() => {
                handleGuest({ target: { value: "" } });
              }}
            ></span>
          )}
          <input
            className="uk-search-input"
            type="search"
            placeholder="Name or Property ID"
            value={search}
            onChange={handleGuest}
          />
        </div>
      </div>
      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          className="chat-guest-contact"
          ref={scrollContainerRef}
          style={{ flex: 1, minHeight: 0, overflowY: "auto" }}
        >
          {normalizedSearch === "" ? (
            allThreads.length === 0 && !loadingMore ? (
              <div className="uk-margin-top">
                There are no messages in this folder
              </div>
            ) : (
              <>
                {allThreads?.map((thread) => {
                  let threadType = getThreadType(thread);
                  if (threadType === "sms") {
                    return (
                      <SMSThreadCard
                        thread={thread}
                        selected={thread.id === selectedThreadId}
                        handleSelect={handleSelect}
                        togglePersonalMeta={togglePersonalMeta}
                        setPersonalMeta={setPersonalMeta}
                      />
                    );
                  } else if (threadType === "airbnb") {
                    return (
                      <AirbnbThreadCard
                        thread={thread}
                        handleSelect={handleSelect}
                        togglePersonalMeta={togglePersonalMeta}
                        setPersonalMeta={setPersonalMeta}
                      />
                    );
                  }
                })}

                {loadingMore && (
                  <div className="uk-text-center uk-margin-small">
                    <div uk-spinner="ratio: 0.8"></div>
                  </div>
                )}
              </>
            )
          ) : !hasActiveSearch ? (
            <div className="uk-margin-top">Type at least 2 characters to search</div>
          ) : reservations?.length === 0 && searchableContacts.length === 0 ? (
            <div className="uk-margin-top">
              There are no messages in this folder
            </div>
          ) : (
            <>
              {reservations?.map((reservation) => {
                return (
                  <ReservationThreadCard
                    reservation={reservation}
                    selected={reservation.thread_id === selectedThreadId}
                    handleSelect={handleSelect}
                    togglePersonalMeta={togglePersonalMeta}
                    setPersonalMeta={setPersonalMeta}
                  />
                );
              })}
              {searchableContacts.map((contact) => {
                return (
                  <ContactSearchThreadCard
                    contact={contact}
                    selected={contact.thread_id === selectedThreadId}
                    handleSelect={handleSelect}
                  />
                );
              })}
              {loadingMore && (
                <div className="uk-text-center uk-margin-small">
                  <div uk-spinner="ratio: 0.8"></div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </aside>
  );
};
